import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { clsx } from "clsx";
import { useGraphiQLActions, useGraphiQL } from "../provider.js";
import { KeycapHint, MODIFIER } from "../keycap-hint/index.js";
import { Tooltip } from "../tooltip/index.js";
import { DropdownMenu } from "../dropdown-menu/index.js";
import { StopIcon, PlayIcon, ChevronDownIcon } from "../../icons/index.js";
import { resolveActiveOperation, getRunBlockReason } from "../../utility/run-block.js";
/* empty css          */
const ExecuteButton = () => {
  const {
    run,
    stop,
    setOperationName
  } = useGraphiQLActions();
  const isFetching = useGraphiQL((state) => state.isFetching);
  const isSubscribed = useGraphiQL((state_0) => Boolean(state_0.subscription));
  const operations = useGraphiQL((state_1) => state_1.operations);
  const operationName = useGraphiQL((state_2) => state_2.operationName);
  const overrideOperationName = useGraphiQL((state_3) => state_3.overrideOperationName);
  const transportMethod = useGraphiQL((state_4) => state_4.transportMethod);
  const runDisabledReason = useGraphiQL((state_5) => getRunBlockReason(state_5.transportMethod, resolveActiveOperation(state_5.operations, state_5.operationName)));
  return /* @__PURE__ */ jsx(ExecuteButtonView, { isFetching, isSubscribed, operations, operationName, overrideOperationName, transportMethod, runDisabledReason, onRun: run, onStop: stop, onSetOperationName: setOperationName });
};
const ExecuteButtonView = ({
  isFetching,
  isSubscribed = false,
  runDisabledReason = null,
  operations = [],
  operationName = null,
  overrideOperationName = null,
  transportMethod = null,
  onRun,
  onStop,
  onSetOperationName
}) => {
  const isRunning = isFetching || isSubscribed;
  const isBlocked = !isRunning && runDisabledReason !== null;
  const hasOptions = operations.length > 1 && typeof overrideOperationName !== "string";
  const showCaret = hasOptions && !isRunning;
  const activeOperation = resolveActiveOperation(operations, operationName);
  const selectOperation = (selectedOperationName) => {
    if (selectedOperationName && selectedOperationName !== operationName) {
      onSetOperationName == null ? void 0 : onSetOperationName(selectedOperationName);
    }
    onRun();
  };
  const primaryButton = /* @__PURE__ */ jsxs("button", { type: "button", className: clsx("graphiql-execute-button-primary", !showCaret && "graphiql-execute-button-primary--solo"), onClick: isRunning ? onStop : onRun, disabled: isBlocked, "aria-label": isRunning ? "Stop query" : "Run query", children: [
    isRunning ? /* @__PURE__ */ jsx(StopIcon, { className: "graphiql-execute-button-icon", "aria-hidden": "true" }) : /* @__PURE__ */ jsx(PlayIcon, { className: "graphiql-execute-button-icon", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx("span", { className: "graphiql-execute-button-label", children: isRunning ? "Stop" : "Run" }),
    /* @__PURE__ */ jsx("span", { className: "graphiql-execute-button-sep", "aria-hidden": "true" }),
    /* @__PURE__ */ jsx(KeycapHint, { keys: [MODIFIER.Meta, MODIFIER.Enter], ariaLabel: isRunning ? "Stop query shortcut" : "Run query shortcut" })
  ] });
  return /* @__PURE__ */ jsxs("div", { className: "graphiql-execute-button", children: [
    isBlocked ? /* @__PURE__ */ jsx(Tooltip, { label: runDisabledReason, children: /* @__PURE__ */ jsx("span", { className: "graphiql-execute-button-tooltip-target", tabIndex: 0, children: primaryButton }) }) : primaryButton,
    showCaret && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("span", { className: "graphiql-execute-button-sep", "aria-hidden": "true" }),
      /* @__PURE__ */ jsxs(DropdownMenu, { children: [
        /* @__PURE__ */ jsx(DropdownMenu.Button, { type: "button", className: "graphiql-execute-button-caret", "aria-label": "Choose operation to run", children: /* @__PURE__ */ jsx(ChevronDownIcon, { className: "graphiql-execute-button-caret-icon", "aria-hidden": "true" }) }),
        /* @__PURE__ */ jsx(DropdownMenu.Content, { align: "end", children: operations.map((operation, i) => {
          var _a;
          const opName = (_a = operation.name) == null ? void 0 : _a.value;
          const label = opName ?? `<Unnamed ${operation.operation}>`;
          const isActive = operation === activeOperation;
          return /* @__PURE__ */ jsx(DropdownMenu.Item, { disabled: getRunBlockReason(transportMethod, operation) !== null, onSelect: () => selectOperation(opName), children: /* @__PURE__ */ jsx("span", { className: clsx("graphiql-execute-button-menu-item", isActive && "graphiql-execute-button-menu-item--active"), children: label }) }, `${label}-${i}`);
        }) })
      ] })
    ] })
  ] });
};
export {
  ExecuteButton,
  ExecuteButtonView
};
//# sourceMappingURL=index.js.map
